import React, { FC, useRef } from 'react';

import {
    Button,
    Upload,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type {
    RcFile,
    UploadFile as AntUploadedFile,
} from 'antd/lib/upload/interface';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { useStores } from '../../../../hooks';
import {
    captureErrorForSentry,
    validateFilename,
    validateFileExtension,
    validateFileObject,
    validateFileSize,
    validateUploadFilesCount,
    buildUploadAcceptAttribute,
} from '../../../../utils';
import { parseFolderPath } from '@/stores/UploadFilesStore/helpers';
import type { OSFolder } from '@/stores/UploadFilesStore/interfaces';
import styles from './OpenUploaderButton.module.scss';

const nameSpace = 'myFiles.header.buttons';

const OpenUploaderButton: FC = observer(() => {
    const { t } = useTranslation();
    const {
        appStore,
        filesListStore,
        uploadFilesStore,
    } = useStores();

    // antd fires `beforeUpload` for every file of a picker batch before the
    // InsertionBuffer has flushed any of them into the store, so
    // `allFilesCount` alone would let a single large selection bypass the
    // upload limit. Track a running total per batch instead (same fix as
    // TableDropZone, see SPX-5931).
    const runningCountRef = useRef(0);

    const onBeforeUpload = async (file: RcFile, fileList: RcFile[]): Promise<boolean | typeof Upload.LIST_IGNORE | void> => {
        const {
            allFilesCount,
            uploadLimit,
            appendFile,
            runCreateFolderActions,
        } = uploadFilesStore;

        // First file of a new batch: re-sync the running total with the store.
        if (fileList[0] === file) {
            runningCountRef.current = allFilesCount;
        }

        const [parentFolder, pathToRoot] = parseFolderPath(file.webkitRelativePath);
        let counted = false;
        try {
            validateFilename(file.name, 'uploadFiles.messages.error');
            validateFileExtension(file.name, appStore.supportedUploadExtensions);
            validateFileSize(file, uploadLimit);
            // The count check and increment stay in the synchronous section so
            // they run in batch order before any of the parallel `await`s below.
            validateUploadFilesCount(runningCountRef.current);
            runningCountRef.current += 1;
            counted = true;
            if (parentFolder) {
                validateFilename(parentFolder, 'createFolder.messages.error');
            } else {
                await validateFileObject(file);
            }
        } catch {
            // validators surface user-facing messages; skip this file.
            // LIST_IGNORE (not `false`) is required so antd does not keep the
            // rejected file in its upload batch — which left folder uploads
            // spinning after only the allowed files finished.
            if (counted) {
                runningCountRef.current -= 1;
            }
            return Upload.LIST_IGNORE;
        }

        appStore.openMyFilesUploadPopup();
        const folderToUploadId = filesListStore.getPossibleUploadFolderId();
        const folder: OSFolder = parentFolder ? { parentFolder, pathToRoot } : null;
        await appendFile({ file, myFilesParentFolderId: folderToUploadId, folder });
        if (parentFolder) {
            await runCreateFolderActions(file);
        }
        // Returning nothing lets antd proceed to `customRequest`, which performs
        // the actual upload. Returning `false` / LIST_IGNORE skips the upload.
        return undefined;
    };

    const uploadAccept = buildUploadAcceptAttribute(appStore.supportedUploadExtensions);

    const uploadControlsProps = {
        multiple: true,
        showUploadList: false,
        accept: uploadAccept,
        fileList: uploadFilesStore.uploadedFilesToJS as AntUploadedFile[],
        beforeUpload: onBeforeUpload,
        customRequest: async ({ file }) => {
            uploadFilesStore.uploadFile(file as RcFile).catch((error) => {
                captureErrorForSentry(error, 'OpenUploaderButton.uploadFile');
            });
        },
    };

    // A single native OS picker cannot select both files and a folder, so we
    // expose two dedicated buttons: one for files, one for whole folders.
    return (
        <>
            <Upload directory={false} {...uploadControlsProps}>
                <Button
                    data-testid="myFilesHeader_addFilesButton"
                    className={styles['open-button']}
                    icon={<PlusOutlined className={styles.icon} />}
                >
                    {t(`${nameSpace}.addFiles`)}
                </Button>
            </Upload>
            <Upload directory {...uploadControlsProps}>
                <Button
                    data-testid="myFilesHeader_addFoldersButton"
                    className={styles['open-button']}
                    icon={<PlusOutlined className={styles.icon} />}
                >
                    {t(`${nameSpace}.addFolders`)}
                </Button>
            </Upload>
        </>
    );
});

export default OpenUploaderButton;
